import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_VIEW_URL,
  CompoundEntityRef,
  Entity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import Edit from '@material-ui/icons/Edit';
import OpenInNew from '@material-ui/icons/OpenInNew';
import React, { useEffect, useState } from 'react';
import { TableProps } from '@backstage/core-components';
import { useAnalytics } from '@backstage/core-plugin-api';
import StarBorder from '@material-ui/icons/StarBorder';
import { withStyles } from '@material-ui/core/styles';
import Star from '@material-ui/icons/Star';
import Add from '@material-ui/icons/Add';
import Repeat from '@material-ui/icons/Repeat';

export const YellowStar = withStyles({
  root: {
    color: '#f3ba37',
  },
})(Star);

export const YellowAdd = withStyles({
  root: {
    color: '#f3ba37',
  },
})(Add);

export const YellowRepeat = withStyles({
  root: {
    color: '#f3ba37',
  },
})(Repeat);

export function useDefaultCatalogTableActions<T extends { entity: Entity }>({
  isStarredEntity,
  toggleStarredEntity,
  isMayContributeEntity,
  toggleMayContributeEntity,
  isMayReuseEntity,
  toggleMayReuseEntity,
}: {
  isStarredEntity: (
    entityOrRef: string | Entity | CompoundEntityRef,
  ) => boolean;
  toggleStarredEntity: (
    entityOrRef: string | Entity | CompoundEntityRef,
  ) => void;
  isMayContributeEntity: (
    entityOrRef: string | Entity | CompoundEntityRef,
  ) => boolean;
  toggleMayContributeEntity: (
    entityOrRef: string | Entity | CompoundEntityRef,
  ) => void;
  isMayReuseEntity: (
    entityOrRef: string | Entity | CompoundEntityRef,
  ) => boolean;
  toggleMayReuseEntity: (
    entityOrRef: string | Entity | CompoundEntityRef,
  ) => void;
}) {
  const analytics = useAnalytics();

  const [defaultActions, setDefaultActions] = useState(
    [] as TableProps<T>['actions'],
  );
  useEffect(() => {
    setDefaultActions([
      ({ entity }: T) => {
        const url = entity.metadata.annotations?.[ANNOTATION_VIEW_URL];
        return {
          icon: () => <OpenInNew aria-label="View" fontSize="small" />,
          tooltip: 'View',
          disabled: !url,
          onClick: () => {
            analytics.captureEvent('click', 'view', {
              attributes: {
                event_category: 'click',
                event_label: 'view',
                value: stringifyEntityRef(entity),
                entityRef: stringifyEntityRef(entity),
              },
            });

            if (!url) return;
            window.open(url, '_blank');
          },
        };
      },
      ({ entity }: T) => {
        const url = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
        return {
          icon: () => <Edit aria-label="Edit" fontSize="small" />,
          tooltip: 'Edit',
          disabled: !url,
          onClick: () => {
            analytics.captureEvent('click', 'edit', {
              attributes: {
                event_category: 'click',
                event_label: 'edit',
                value: stringifyEntityRef(entity),
                entityRef: stringifyEntityRef(entity),
              },
            });
            if (!url) return;
            window.open(url, '_blank');
          },
        };
      },
      ({ entity }: T) => {
        const isStarred = isStarredEntity(entity);
        return {
          cellStyle: { paddingLeft: '1em' },
          icon: () => (isStarred ? <YellowStar /> : <StarBorder />),
          tooltip: isStarred ? 'Remove from favorites' : 'Add to favorites',
          onClick: () => {
            analytics.captureEvent('click', 'playlist', {
              attributes: {
                entityRef: stringifyEntityRef(entity),
                value: stringifyEntityRef(entity),
                event_category: 'click',
                event_label: 'playlist',
                event_type: 'starred',
                selected: !isStarred,
              },
            });
            toggleStarredEntity(entity);
          },
        };
      },
      ({ entity }: T) => {
        const isAddedToMayContribute = isMayContributeEntity(entity);
        return {
          cellStyle: { paddingLeft: '1em' },
          icon: () => (isAddedToMayContribute ? <YellowAdd /> : <Add />),
          tooltip: isAddedToMayContribute
            ? 'Remove from May Contribute List'
            : 'Add to May Contribute List',
          onClick: () => {
            analytics.captureEvent('click', 'playlist', {
              attributes: {
                event_category: 'click',
                event_label: 'playlist',
                event_type: 'may_contribute',
                value: stringifyEntityRef(entity),
                entityRef: stringifyEntityRef(entity),
                selected: !isAddedToMayContribute,
              },
            });
            toggleMayContributeEntity(entity);
          },
        };
      },
      ({ entity }: T) => {
        const isAddedToMayReuse = isMayReuseEntity(entity);
        return {
          cellStyle: { paddingLeft: '1em' },
          icon: () => (isAddedToMayReuse ? <YellowRepeat /> : <Repeat />),
          tooltip: isAddedToMayReuse
            ? 'Remove from May Reuse List'
            : 'Add to May Reuse List',
          onClick: () => {
            analytics.captureEvent('click', 'playlist', {
              attributes: {
                event_category: 'click',
                event_label: 'playlist',
                event_type: 'may_reuse',
                value: stringifyEntityRef(entity),
                entityRef: stringifyEntityRef(entity),
                selected: !isAddedToMayReuse,
              },
            });
            toggleMayReuseEntity(entity);
          },
        };
      },
    ]);
  }, [
    isStarredEntity,
    isMayContributeEntity,
    isMayReuseEntity,
    setDefaultActions,
    toggleStarredEntity,
    toggleMayContributeEntity,
    toggleMayReuseEntity,
  ]);
  return { defaultActions };
}
